import { isNil } from './helpers'
import { GetServerSidePropsContext } from 'next'
import { ListRouterQuery } from '@/interfaces/filters'

export const allowedParams = ['viewMode', 'sortBy', 'time', 'search', 'scope', 'limit']
export const storedParamsKeys = { viewMode: 'defaultViewMode', sortBy: 'activeSorting', time: 'activeTimeFilter' }

const getClearPathnameWithSearchParams = (path: string): { pathname: string; search: string } => {
  const { pathname, search } = new URL(path, 'http://localhost')
  const newPathname = pathname.replace(/\/pages\/\d+/, '')
  return { pathname: newPathname, search }
}

export const getPageHref = (pageNumber: number, path: string): string => {
  // eslint-disable-next-line eqeqeq
  if (isNil(pageNumber)) {
    return '#'
  }
  const { pathname, search } = getClearPathnameWithSearchParams(path)
  const newPathname = pathname + `pages/${pageNumber}/`
  return `${newPathname}${search}`
}

export const getViewModeHref = (viewMode: string, path: string): string => {
  const { pathname, search } = new URL(path, 'http://localhost')
  const searchParams = new URLSearchParams(search)
  searchParams.set('viewMode', viewMode)
  return `${pathname}?${searchParams.toString()}`
}

export const getTimeFilterHref = (timeFilter: string, path: string): string => {
  const { pathname, search } = getClearPathnameWithSearchParams(path)
  const searchParams = new URLSearchParams(search)
  searchParams.set('time', timeFilter)
  return `${pathname}?${searchParams.toString()}`
}

export const getSortByFilterHref = (sortByFilter: string, path: string): string => {
  const { pathname, search } = getClearPathnameWithSearchParams(path)
  const searchParams = new URLSearchParams(search)
  searchParams.set('sortBy', sortByFilter)
  return `${pathname}?${searchParams.toString()}`
}

export const getSearchFilterHref = (searchFilter: string, path: string): string => {
  const { pathname, search } = getClearPathnameWithSearchParams(path)
  const searchParams = new URLSearchParams(search)
  searchParams.set('search', searchFilter)
  return `${pathname}?${searchParams.toString()}`
}

export const isValidViewMode = (viewMode: string): boolean => {
  return ['thumbView', 'listView'].includes(viewMode)
}

export const isValidTimeFilter = (timeFilter: string): boolean => {
  return ['all', 'week', 'month', 'year'].includes(timeFilter)
}

export const isValidSortByFilter = (sortByFilter: string): boolean => {
  return ['project_popular', 'project_views', 'project_copies', 'project_creationdate', 'project_lastchange'].includes(
    sortByFilter
  )
}

export const isValidFilterValue = (key: string, value: string): boolean => {
  if (key === 'viewMode') {
    return isValidViewMode(value)
  }
  if (key === 'sortBy') {
    return isValidSortByFilter(value)
  }
  if (key === 'time') {
    return isValidTimeFilter(value)
  }
  return true
}

export const getFiltersFromQuery = (
  query: ListRouterQuery,
  req: GetServerSidePropsContext['req'],
  ignore?: {
    time?: boolean
  }
): {
  filters: ListRouterQuery
  shouldRedirect: boolean
} => {
  let shouldRedirect = false
  const allowedQuery: ListRouterQuery = {}
  const filters: ListRouterQuery = {}

  Object.keys(query).forEach((key) => {
    if (allowedParams.includes(key)) {
      allowedQuery[key] = query[key]
    }
  })

  Object.keys(storedParamsKeys).forEach((key) => {
    const storedValue = req.cookies[storedParamsKeys[key]]
    if (!isNil(storedValue) && isValidFilterValue(key, storedValue)) {
      filters[key] = storedValue
    }
  })

  Object.keys(allowedQuery).forEach((key) => {
    const queryValue = allowedQuery[key] as string
    if (!isNil(queryValue) && isValidFilterValue(key, queryValue)) {
      filters[key] = queryValue
    } else {
      shouldRedirect = true
    }
  })

  if (ignore?.time) {
    delete filters.time
    delete allowedQuery.time
  }

  Object.keys(filters).forEach((key) => {
    if (!isNil(filters[key]) && filters[key] !== allowedQuery[key]) {
      shouldRedirect = true
    }
  })

  return {
    filters,
    shouldRedirect
  }
}
